import api from './api';

export interface CalendarEvent {
  _id: string;
  userId: string;
  title: string;
  description?: string;
  startTime: string;
  endTime: string;
  allDay: boolean;
  location?: string;
  eventType: 'appointment' | 'meeting' | 'open_house' | 'inspection' | 'follow_up' | 'other';
  status: 'scheduled' | 'confirmed' | 'cancelled' | 'completed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  relatedProperty?: {
    _id: string;
    address: string;
    suburb: string;
    state: string;
    postcode: string;
  };
  relatedCustomer?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  relatedWorkflow?: {
    _id: string;
    name: string;
  };
  attendees?: Array<{
    userId?: string;
    name: string;
    email: string;
    phone?: string;
    role: 'organizer' | 'attendee' | 'optional';
    responseStatus: 'pending' | 'accepted' | 'declined' | 'tentative';
  }>;
  isRecurring: boolean;
  recurringPattern?: {
    type: 'daily' | 'weekly' | 'monthly' | 'yearly';
    interval: number;
    endDate?: string;
    daysOfWeek?: number[];
    dayOfMonth?: number;
  };
  notifications: Array<{
    type: 'email' | 'in_app' | 'sms' | 'both';
    minutesBefore: number;
    sent: boolean;
    sentAt?: string;
  }>;
  reminderEnabled: boolean;
  reminderMinutes: number;
  notes?: string;
  attachments?: Array<{
    name: string;
    url: string;
    type: string;
    size: number;
  }>;
  color?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface CreateCalendarEventData {
  title: string;
  description?: string;
  startTime: string;
  endTime: string;
  allDay?: boolean;
  location?: string;
  eventType?: 'appointment' | 'meeting' | 'open_house' | 'inspection' | 'follow_up' | 'other';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  relatedProperty?: string;
  relatedCustomer?: string;
  relatedWorkflow?: string;
  attendees?: Array<{
    userId?: string;
    name: string;
    email: string;
    phone?: string;
    role?: 'organizer' | 'attendee' | 'optional';
  }>;
  isRecurring?: boolean;
  recurringPattern?: {
    type: 'daily' | 'weekly' | 'monthly' | 'yearly';
    interval: number;
    endDate?: string;
    daysOfWeek?: number[];
    dayOfMonth?: number;
  };
  notifications?: Array<{
    type: 'email' | 'in_app' | 'sms' | 'both';
    minutesBefore: number;
  }>;
  reminderEnabled?: boolean;
  reminderMinutes?: number;
  notes?: string;
  color?: string;
}

export interface UpdateCalendarEventData extends Partial<CreateCalendarEventData> {}

export interface CalendarStats {
  totalEvents: number;
  upcomingEvents: number;
  byType: Array<{
    _id: string;
    count: number;
    totalDuration: number;
  }>;
  timeRange: {
    start?: string;
    end?: string;
  };
}

export interface AgencyUser {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  primaryRole: string;
}

class CalendarService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = '/agencies';
  }

  private getAgencyUrl(agencySlug: string): string {
    return `${this.baseUrl}/${agencySlug}/calendar`;
  }

  // Create a new calendar event
  async createEvent(agencySlug: string, eventData: CreateCalendarEventData): Promise<CalendarEvent> {
    const response = await api.post(`${this.getAgencyUrl(agencySlug)}/events`, eventData);
    return response.data.data;
  }

  // Get calendar events with optional filters
  async getEvents(
    agencySlug: string,
    filters?: {
      start?: string;
      end?: string;
      eventType?: string;
      status?: string;
      relatedProperty?: string;
      relatedCustomer?: string;
      userId?: string;
      view?: 'agency' | 'personal';
    }
  ): Promise<CalendarEvent[]> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
    }

    const response = await api.get(`${this.getAgencyUrl(agencySlug)}/events?${params.toString()}`);
    return response.data.data;
  }

  // Get a specific calendar event
  async getEvent(agencySlug: string, eventId: string): Promise<CalendarEvent> {
    const response = await api.get(`${this.getAgencyUrl(agencySlug)}/events/${eventId}`);
    return response.data.data;
  }

  // Update a calendar event
  async updateEvent(agencySlug: string, eventId: string, eventData: UpdateCalendarEventData): Promise<CalendarEvent> {
    const response = await api.put(`${this.getAgencyUrl(agencySlug)}/events/${eventId}`, eventData);
    return response.data.data;
  }

  // Delete a calendar event
  async deleteEvent(agencySlug: string, eventId: string): Promise<void> {
    await api.delete(`${this.getAgencyUrl(agencySlug)}/events/${eventId}`);
  }

  // Get upcoming events
  async getUpcomingEvents(agencySlug: string, limit: number = 10): Promise<CalendarEvent[]> {
    const response = await api.get(`${this.getAgencyUrl(agencySlug)}/events/upcoming?limit=${limit}`);
    return response.data.data;
  }

  // Respond to calendar invitation
  async respondToInvitation(
    agencySlug: string,
    eventId: string,
    responseStatus: 'accepted' | 'declined' | 'tentative'
  ): Promise<CalendarEvent> {
    const response = await api.post(`${this.getAgencyUrl(agencySlug)}/events/${eventId}/respond`, {
      responseStatus
    });
    return response.data.data;
  }

  // Get calendar statistics
  async getStats(
    agencySlug: string,
    filters?: {
      start?: string;
      end?: string;
      userId?: string;
      view?: 'agency' | 'personal';
    }
  ): Promise<CalendarStats> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
    }

    const response = await api.get(`${this.getAgencyUrl(agencySlug)}/stats?${params.toString()}`);
    return response.data.data;
  }

  // Get agency users (admin only)
  async getAgencyUsers(agencySlug: string): Promise<AgencyUser[]> {
    const response = await api.get(`${this.getAgencyUrl(agencySlug)}/users`);
    return response.data.data;
  }

  // Helper method to format event for calendar display
  formatEventForCalendar(event: CalendarEvent) {
    return {
      id: event._id,
      title: event.title,
      start: new Date(event.startTime),
      end: new Date(event.endTime),
      allDay: event.allDay,
      color: event.color || this.getEventTypeColor(event.eventType),
      extendedProps: {
        description: event.description,
        location: event.location,
        eventType: event.eventType,
        status: event.status,
        priority: event.priority,
        relatedProperty: event.relatedProperty,
        relatedCustomer: event.relatedCustomer,
        attendees: event.attendees,
        notes: event.notes
      }
    };
  }

  // Get color for event type
  private getEventTypeColor(eventType: string): string {
    switch (eventType) {
      case 'appointment':
        return '#1976d2';
      case 'meeting':
        return '#388e3c';
      case 'open_house':
        return '#f57c00';
      case 'inspection':
        return '#7b1fa2';
      case 'follow_up':
        return '#d32f2f';
      default:
        return '#757575';
    }
  }

  // Helper method to check if event is upcoming
  isEventUpcoming(event: CalendarEvent): boolean {
    const now = new Date();
    const eventTime = new Date(event.startTime);
    const reminderTime = new Date(eventTime.getTime() - (event.reminderMinutes * 60 * 1000));
    return now >= reminderTime && now < eventTime && event.status === 'scheduled';
  }

  // Helper method to check if event is overdue
  isEventOverdue(event: CalendarEvent): boolean {
    const now = new Date();
    const eventTime = new Date(event.endTime);
    return now > eventTime && event.status === 'scheduled';
  }

  // Helper method to get time until event
  getTimeUntilEvent(event: CalendarEvent): string {
    const now = new Date();
    const eventTime = new Date(event.startTime);
    const diffMs = eventTime.getTime() - now.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    
    if (diffMins < 60) {
      return `${diffMins} minutes`;
    } else if (diffMins < 1440) {
      const hours = Math.floor(diffMins / 60);
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    } else {
      const days = Math.floor(diffMins / 1440);
      return `${days} day${days > 1 ? 's' : ''}`;
    }
  }
}

export default new CalendarService(); 